"use client";

import { cn } from "@/utils/cn";
import { useEffect, useRef, useState } from "react";

interface LazySectionProps {
	children: React.ReactNode;
	className?: string;
	rootMargin?: string;
	threshold?: number;
	fallback?: React.ReactNode;
	reserveHeight?: number | string; // Pre-allocate height to prevent CLS
	/** When true, render children during SSR (before intersection). */
	ssrShowChildren?: boolean;
}

/**
 * LazySection component that only renders children when they come into viewport
 * Uses Intersection Observer for performance optimization
 */
export function LazySection({
	children,
	className,
	rootMargin = "50px",
	threshold = 0.1,
	fallback,
	reserveHeight,
	ssrShowChildren = false,
}: LazySectionProps) {
	// To avoid hydration mismatches, when ssrShowChildren is true, ensure
	// the client also initially renders children (isVisible = true).
	const [isVisible, setIsVisible] = useState<boolean>(ssrShowChildren);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// If already visible (SSR or previously intersected), skip observing
		if (isVisible) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsVisible(true);
					observer.disconnect();
				}
			},
			{
				rootMargin,
				threshold,
			},
		);

		if (ref.current) {
			observer.observe(ref.current);
		}

		return () => observer.disconnect();
	}, [isVisible, rootMargin, threshold]);

	const reservedStyle = !isVisible
		? {
				minHeight: typeof reserveHeight === "number" ? `${reserveHeight}px` : reserveHeight || undefined,
			}
		: undefined;

	return (
		<div ref={ref} className={cn(className)} style={reservedStyle}>
			{isVisible ? children : fallback || <div className="min-h-[200px]" />}
		</div>
	);
}
